import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { discordLink, isLaunched, signupPage } from '../../Constants';
import LogoCollection from './LogoCollection';
import AppLink from '../generic/AppLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { useColorScheme, useTheme } from '@mui/material/styles';
import { BetaChip } from '../generic/BetaChip';
import Starfield from '../bits/Starfield';
import { BlurFade } from '../../animations/BlurFade';

const StyledBox = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  width: '104%',
  height: 225,
  marginTop: theme.spacing(4),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '16px',
    border: '2px solid #121212',
    pointerEvents: 'none',
    zIndex: 2,
    maskImage: `linear-gradient(
      to top,
      transparent 0%,
      rgba(255,255,255,0.20) 50%,
      rgba(255,255,255,0.45) 60%,
      rgba(255,255,255,0.85) 85%,
      rgb(255,255,255) 95%
    )`,
    WebkitMaskImage: `linear-gradient(
      to top,
      transparent 0%,
      rgba(255,255,255,0.20) 50%,
      rgba(255,255,255,0.45) 60%,
      rgba(255,255,255,0.85) 85%,
      rgb(255,255,255) 95%
    )`,
  },
  [theme.breakpoints.between('sm', 'md')]: {
    height: 350,
  },
  [theme.breakpoints.up('md')]: {
    height: 550,
    width: '83%',
    margin: '0 auto',
    marginTop: theme.spacing(4),
  },
}));

const HeroImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center top',
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    objectPosition: 'left top',
    width: '160%',
    marginRight: '-50%',
  },
}));

const GradientOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(
    to bottom,
    transparent 30%,
    rgba(0,0,0,0.20) 50%,
    rgba(0,0,0,0.45) 60%,
    rgba(0,0,0,0.85) 85%,
    rgb(0,0,0) 95%
  )`,
  pointerEvents: 'none',
  ...theme.applyStyles('dark', {

  }),
}));

/* Gradient
backgroundImage:'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(120, 44.80%, 45.50%), transparent)',
        ...theme.applyStyles('dark', {
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(120, 100%, 16%), transparent)',
        }),
*/
export default function Hero() {
  const { mode } = useColorScheme();
  const theme = useTheme();

  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: '100%',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden'
      })}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 16 },
          pb: { xs: 0, sm: 0 },
          position: 'relative',
        }}
      >
        {/*<Box sx={{ width: '100%', height: '450px', position: 'absolute', top: 110, left: 0, right: 0, zIndex: 0, filter: 'blur(3px)', pointerEvents: 'none' }}>
          <Orb
            hoverIntensity={0}
            rotateOnHover={false}
            hue={140}
            forceHoverState={false}
          />
        </Box>*/}

        <Stack
          spacing={3}
          useFlexGap
          sx={{ alignItems: 'center', width: { xs: '100%', sm: '70%' }, position: 'relative', zIndex: 1, mt: 6 }}
        >
          <BlurFade style={{ width: '100%' }} delay={.35} inView>
            <BetaChip />
          </BlurFade>
          <BlurFade style={{ width: '100%', alignItems: 'center' }} delay={.10} inView>
            <Typography
              variant="h1"
              sx={(theme) => ({
                textAlign: 'center',
                fontSize: 'clamp(2.75rem, 9vw, 3.3rem)',
                fontWeight: 500,
                color: '#FFFFFF',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                [theme.breakpoints.up('lg')]: {
                  fontSize: 'clamp(3.5rem, 6vw, 3.95rem)'
                },
              })}
            >
              The ultimate all-in-one journaling software
            </Typography>
          </BlurFade>
          <BlurFade style={{ width: '100%', alignItems: 'center' }} delay={.35}>
            <Typography
              sx={{
                textAlign: 'center',
                color: '#5A5A5A',
                width: { sm: '100%', md: '80%' },
                mx: 'auto',
                mt: -1,
                fontWeight: 400,
                fontSize: '1.1rem',
                lineHeight: 1.6,
              }}
            >
              The journal we wish we had so we built it for you.
              <br />
              Journal, analytics and backtesting in a single place.
            </Typography>
          </BlurFade>
          <BlurFade delay={.60}>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  minWidth: 'fit-content',
                  color: '#FFFFFF',
                  backgroundColor: '#181818',
                  border: 'none',
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#222222',
                    boxShadow: 'none',
                    border: 'none',
                  },
                }}
              >
                Watch Demo
              </Button>
              <AppLink href={signupPage}>
                <Button
                  variant="contained"
                  color='primary'
                  size="small"
                  sx={{
                    minWidth: 'fit-content',
                  }}
                >
                  Get Started
                </Button>
              </AppLink>
            </Stack>
          </BlurFade>
        </Stack>

        <BlurFade sx={{ width: '100%', marginRight: { xs: 0, sm: '50px' }, mt: 12 }} delay={.85} inView inViewMargin='0px'>
          <Typography
            sx={{
              textAlign: 'right',
              fontSize: '0.8rem',
              fontStyle: 'italic',
              color: 'rgba(255,255,255,0.4)',
              mb: -3.5,
              pr: { md: 'calc((100% - 83%) / 2)' },
            }}
          >
            P&L Calendar
          </Typography>
          <StyledBox id="image">
            <HeroImage
              src={`/images/hero_dark.webp`}
              alt="Calendar"
            />
            <GradientOverlay />
          </StyledBox>
        </BlurFade>

        <BlurFade sx={{ width: '100%', alignItems: 'center' }} delay={.85}>
          {/* Charts by TradingView attribution */}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1, mb: 2, cursor: 'pointer' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              Charts by
            </Typography>
            <Link
              href="https://www.tradingview.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <Box
                component="img"
                src="/images/tradingview.webp"
                alt="TradingView"
                sx={{ width: 150, mt: 0.25, display: 'inline-block' }}
              />
            </Link>
          </Box>

          <LogoCollection />
        </BlurFade>
      </Container>

    </Box >
  );
}